import React, { useState, useCallback, useRef, useLayoutEffect } from 'react';
import { toFullMediaUrl, toFullMediaThumb, toFullMediaSrcSet } from '../../utils/mediaUrl';
import './ProgressiveImg.css';

function toBrowserImageUrl(path) {
  const value = String(path || '').trim();
  if (!value) return null;
  // Local previews must stay as blob/data URLs. They are not backend files.
  if (/^(?:blob:|data:)/i.test(value)) return value;
  return toFullMediaUrl(value);
}

/**
 * ProgressiveImg — image with a lightweight placeholder that
 * shows BEFORE the bytes arrive, and a fast fade-in once the
 * image is loaded.
 *
 * Why this matters: the browser paints <img> with no background
 * colour while downloading. On a 4G connection that gives
 * 200-800ms of a black box per image.
 *
 * The placeholder is a subtle diagonal gradient (no hard black)
 * and the image fades in quickly once `onLoad` fires. The
 * browser HTTP cache + Nginx `expires 30d` mean the second visit
 * skips the download entirely.
 *
 * Stored media uses responsive JPEG thumbnails for cards and feed previews.
 * Components that open a full-size viewer can opt into the original with
 * `preferOriginal`; this keeps the first paint fast without sacrificing the
 * quality of the image when it is intentionally opened large.
 */
const ProgressiveImg = ({
  src,
  alt = '',
  eager = false,
  onClick,
  className = '',
  imgClassName = 'gm-progimg__img',
  sizes,
  skipSrcSet = false,
  preferOriginal = false,
  thumbWidth = 640,
  fallback = null,
  onError,
  onLoad,
  imgStyle,
  keepPreviousOnSourceChange = false,
  retainedSrc = null,
}) => {
  const isLocalPreview = /^(?:blob:|data:)/i.test(String(src || '').trim());
  const useOriginal = isLocalPreview || preferOriginal;
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(() => (
    useOriginal
      ? toBrowserImageUrl(src)
      : toFullMediaThumb(src, { width: thumbWidth })
  ));
  const [currentSrcSet, setCurrentSrcSet] = useState(null);
  const imageClassName = ['gm-progimg__img', imgClassName].filter(Boolean).join(' ');
  const imgRef = useRef(null);
  const retriedOriginal = useRef(false);
  const currentSrcRef = useRef(currentSrc);
  const [displayedSrc, setDisplayedSrc] = useState(null);

  const handleLoad = useCallback((event) => {
    if (currentSrcRef.current !== currentSrc) return;
    const image = event.currentTarget;
    const loadedSource = image.currentSrc || image.src || currentSrc;
    onLoad?.(event);
    const revealDecodedImage = () => {
      if (currentSrcRef.current !== currentSrc) return;
      setLoaded(true);
      setDisplayedSrc(loadedSource);
    };
    // Keep the previous decoded frame visible until the incoming bitmap is
    // ready to paint, not merely until its network response has completed.
    if (typeof image.decode === 'function') {
      image.decode().then(revealDecodedImage, revealDecodedImage);
    } else {
      revealDecodedImage();
    }
  }, [currentSrc, onLoad]);
  const handleError = useCallback(() => {
    if (currentSrcRef.current !== currentSrc) return;
    const original = toBrowserImageUrl(src);
    if (original && currentSrc !== original && !retriedOriginal.current) {
      // Keep the original as a reliable fallback for legacy files without a
      // generated thumbnail.
      retriedOriginal.current = true;
      setLoaded(false);
      currentSrcRef.current = original;
      setCurrentSrc(original);
      setCurrentSrcSet(null);
      return;
    }
    setErrored(true);
    setLoaded(true);
    onError?.();
  }, [currentSrc, onError, src]);

  // A URL can change after an upload, profile refresh or feed pagination.
  // Reset the retry state so the new image gets the same quality-aware flow.
  useLayoutEffect(() => {
    retriedOriginal.current = false;
    const nextSrc = useOriginal
      ? toBrowserImageUrl(src)
      : toFullMediaThumb(src, { width: thumbWidth });
    setLoaded(false);
    setErrored(false);
    currentSrcRef.current = nextSrc;
    setCurrentSrc(nextSrc);
    setCurrentSrcSet(useOriginal || skipSrcSet ? null : toFullMediaSrcSet(src));
  }, [keepPreviousOnSourceChange, preferOriginal, skipSrcSet, src, thumbWidth, useOriginal]);

  // If the image is already cached by the browser, `complete` is
  // true on mount and `onLoad` may never fire. Detect that and
  // set loaded=true synchronously so we don't flash the
  // placeholder.
  useLayoutEffect(() => {
    const el = imgRef.current;
    if (currentSrcRef.current !== currentSrc) return;
    if (el && el.complete && el.naturalWidth > 0) {
      setLoaded(true);
      setDisplayedSrc(el.currentSrc || el.src || currentSrc);
    }
  }, [currentSrc]);

  const retainedUrl = retainedSrc ? toBrowserImageUrl(retainedSrc) : null;
  const previousCandidate = displayedSrc || retainedUrl;
  const previousSrc = keepPreviousOnSourceChange && !loaded && previousCandidate !== currentSrc
    ? previousCandidate
    : null;
  const hasUsablePrevious = Boolean(previousSrc);

  if (errored && !hasUsablePrevious) {
    return (
      <div className={`gm-progimg gm-progimg--error ${className}`} onClick={onClick}>
        {fallback || <div className="gm-progimg__error-mark" aria-hidden="true">!</div>}
      </div>
    );
  }

  return (
    <div
      className={`gm-progimg ${loaded ? 'is-loaded' : ''} ${className}`}
      onClick={onClick}
    >
      {previousSrc && (
        <img
          className={`${imageClassName} gm-progimg__img--previous`}
          src={previousSrc}
          alt={alt}
          aria-hidden="true"
          draggable={false}
        />
      )}
      {!errored && (
        <img
          ref={imgRef}
          key={currentSrc || 'empty-image'}
          className={imageClassName}
          src={currentSrc || undefined}
          srcSet={currentSrcSet || undefined}
          sizes={currentSrcSet ? sizes : undefined}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          // The first visible image is explicitly promoted by the caller;
          // below-the-fold media should stay out of the critical network
          // path so it cannot compete with the LCP image.
          fetchPriority={eager ? 'high' : 'low'}
          decoding="async"
          draggable={false}
          style={imgStyle}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
    </div>
  );
};

export default ProgressiveImg;
